"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { EventPageData } from "@/actions/event";
import { getExchangeRate } from "@/actions/forex";
import type { PackForSaleRow } from "@/actions/packs";
import type { TaggableEvent } from "@/actions/post-context";
import type { EntityStoryStatus } from "@/actions/story";
import { useAuthPrompt } from "@/components/auth";
import type { CommentThreadViewer } from "@/components/posts/comment-thread.client";
import { useVideoAutoplay } from "@/components/providers/video-autoplay-provider";
import { DETAIL_DESKTOP } from "@/components/shell/detail-page-desktop";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { EventAddonsProvider } from "@/features/event-addons/components/event-addons-provider.client";
import { EventAddonsPill } from "@/features/event-addons/components/event-addons-showcase.client";
import { EventHostCredit } from "@/features/hiives/components/event-detail-hub/event-page/event-host-credit";
import type { PackStripContext } from "@/features/packs/components/pack-strip";
import type { ViewerPackCoverage } from "@/features/packs/lib/eligibility";
import { useBackNavigation } from "@/hooks/use-back-navigation";
import {
  DISABLE_DRAWER_ANIMATION_CLASS,
  useDisableInitialDrawerAnimation,
} from "@/hooks/use-disable-initial-drawer-animation";
import { useIsDesktop, useWindowScrolledPast } from "@/hooks/use-is-desktop";
import { useNow } from "@/hooks/use-now";
import {
  captureClientEvent,
  captureIdentifiedClientEvent,
} from "@/lib/analytics/client";
import {
  type EventEntryAttribution,
  resolveEventEntry,
} from "@/lib/analytics/entry-point";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { ASPECT_RATIO_VALUE } from "@/lib/aspect-ratios";
import { eventAvailability } from "@/lib/availability";
import { resolveBackHref } from "@/lib/back-nav";
import { toLocalDateKey } from "@/lib/dates/local-date";
import { formatClockTime, formatDayMonth } from "@/lib/dates/relative-time";
import {
  APP_DEFAULT_TIME_ZONE,
  fromZonedDateInput,
  toZonedDateInput,
} from "@/lib/dates/zoned-input";
import {
  type EventEligibilityGate,
  NO_UPCOMING_DATES_RESTRICTION,
} from "@/lib/event-access";
import type { EventAddonMenu } from "@/lib/events/addon-types";
import { bookingClosedCopy } from "@/lib/events/booking-closed-cta";
import {
  type BookingWindowOccurrence,
  formatMoment,
  resolveBookingWindowState,
} from "@/lib/events/booking-window-state";
import { goBackTo } from "@/lib/go-back";
import { resolveModuleTabLabel } from "@/lib/modules/module-registry";
import { detailSheetSnapPoints } from "@/lib/ui/detail-sheet-snap-points";
import { attachSheetPullHandoff } from "@/lib/ui/sheet-pull-handoff";
import { cn } from "@/lib/utils";
import { EventFooterCta } from "./event-footer-cta";
import { EventHeroSection } from "./event-hero-section";
import {
  type BookingOccurrence,
  getDateKeys,
  getLowestActivePass,
  getOccurrencesForSelectedDate,
  getSelectedOccurrence,
  groupOccurrencesByDate,
  type HiiveSummary,
  isMultiDate,
  normalizeOccurrences,
  toDate,
  withoutCanceledSessions,
} from "./event-page.shared";
import { EventStickyHeader } from "./event-sticky-header";
import { EventTabsSection } from "./event-tabs-section";

interface EventPageClientProps {
  /** Optional extras sold beside the ticket (food, merch); null when none. */
  addons?: EventAddonMenu | null;
  hiive: HiiveSummary;
  event: EventPageData["event"];
  occurrences: BookingOccurrence[];
  bookingRestriction?: string;
  bookingWindow: EventPageData["bookingWindow"];
  canBookEvent: boolean;
  waitlistOnly?: boolean;
  /** True when signing in cannot change the current booking availability. */
  bookingAvailabilityBlocked: boolean;
  /**
   * Signed-out viewer the booking flow will accept: sign-in is deferred to
   * checkout, so the CTA reads "Book tickets" exactly as it does for members.
   */
  canStartGuestBooking: boolean;
  canViewAttendeeList: boolean;
  canViewAttendeeCount: boolean;
  membershipStatus: "active" | "pending" | null;
  meetsTierRequirement: boolean;
  hiiveSlug: string;
  eventSlug: string;
  userId?: string;
  viewer?: CommentThreadViewer;
  hostUserIds: string[];
  /** Viewer's closest confirmed occurrence for this event (Photos tab CTA). */
  viewerAlbumTag: TaggableEvent | null;
  nextOccurrence: {
    id: number;
    start: Date;
    end: Date;
    timezone: string | null;
  } | null;
  allowedTierNames: string[];
  /**
   * Structured reason this viewer cannot book — see `lib/event-access.ts`.
   * Carries the pending-join-request state too, which no membership row does.
   */
  eligibilityGate?: EventEligibilityGate;
  /** The viewer's own pending join request, when the gate is one. */
  pendingTierRequestId?: number | null;
  distanceKm: number | null;
  hiiveMemberCount: number;
  hiiveEventCount: number;
  packs: PackForSaleRow[];
  packStripContext?: PackStripContext | null;
  viewerPackHolding?: ViewerPackCoverage | null;
  storyRingStatus: EntityStoryStatus | null;
  /** Discussion and photo counts for the tab bar. Zero renders no badge. */
  tabCounts: { discussion: number; photos: number };
}

function formatShortDate(date: Date) {
  return formatDayMonth(date, { weekday: true });
}

// Locale tags do not pin CLDR punctuation or day-period casing across engines.
const formatShortTime = (date: Date) => formatClockTime(date).toUpperCase();

function EventDateSummary({
  pattern,
  nextOccurrence,
  dateKeys,
  timesOnNextDate = [],
  openEndedTime,
}: {
  pattern: string;
  nextOccurrence: {
    start: Date;
    end: Date;
    timezone: string | null;
  } | null;
  dateKeys: string[];
  /** Sessions on the next session's day; a one-time event may have several. */
  timesOnNextDate?: Array<{ start: Date; end: Date }>;
  /** Render "9:00 PM onwards" instead of a closing time. */
  openEndedTime?: boolean;
}) {
  if (!nextOccurrence) return null;

  const isMultiDateEvent = isMultiDate(pattern);

  let text: string;
  if (isMultiDateEvent && dateKeys.length > 1) {
    const firstDate =
      fromZonedDateInput(dateKeys[0], APP_DEFAULT_TIME_ZONE) ??
      nextOccurrence.start;
    const lastDate =
      fromZonedDateInput(
        dateKeys[dateKeys.length - 1],
        APP_DEFAULT_TIME_ZONE,
      ) ?? nextOccurrence.end;
    const slotSuffix =
      pattern === "multi_date_multi_slot" ? ", Multiple times" : "";
    text = `${formatShortDate(firstDate)} \u2013 ${formatShortDate(lastDate)}${slotSuffix}`;
  } else if (timesOnNextDate.length > 1) {
    // One day of several batches: one session's times would read as THE time.
    text = `${formatShortDate(nextOccurrence.start)}, ${timesOnNextDate
      .toSorted((a, b) => a.start.getTime() - b.start.getTime())
      .map(
        ({ start, end }) =>
          `${formatShortTime(start)} – ${formatShortTime(end)}`,
      )
      .join(" · ")}`;
  } else if (openEndedTime) {
    /*
     * The end time still exists — booking windows, the live-event card and
     * every "has this finished" check read it — it is simply not a promise the
     * organiser wants to make. A night that ends when it ends says "onwards".
     */
    text = `${formatShortDate(nextOccurrence.start)}, ${formatShortTime(nextOccurrence.start)} onwards`;
  } else if (
    toZonedDateInput(nextOccurrence.start, APP_DEFAULT_TIME_ZONE) !==
    toZonedDateInput(nextOccurrence.end, APP_DEFAULT_TIME_ZONE)
  ) {
    text = `${formatShortDate(nextOccurrence.start)}, ${formatShortTime(nextOccurrence.start)} \u2013 ${formatShortDate(nextOccurrence.end)}, ${formatShortTime(nextOccurrence.end)}`;
  } else {
    text = `${formatShortDate(nextOccurrence.start)}, ${formatShortTime(nextOccurrence.start)} \u2013 ${formatShortTime(nextOccurrence.end)}`;
  }

  return (
    <p
      className="w-full min-w-0 text-sm leading-relaxed font-medium whitespace-normal text-text-secondary dark:text-[#F5E6B8]"
      title={text}
    >
      {text}
    </p>
  );
}

const CURRENCY_FORMATTERS = new Map<string, Intl.NumberFormat>();
const SETTLEMENT_CURRENCY = "INR";
type RatePreview = { rate: number; date: string; fetchedAtIso: string };

const EVENT_TAB_VALUES = new Set([
  "details",
  "attendees",
  "features",
  "discussion",
  "photos",
]);

const normalizeTab = (value: string | null) =>
  value && EVENT_TAB_VALUES.has(value) ? value : "details";

const formatCurrency = (amountMinor: number, currency: string) => {
  const cacheKey = currency;
  let formatter = CURRENCY_FORMATTERS.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: currency === "INR" ? 0 : 2,
      maximumFractionDigits: currency === "INR" ? 0 : 2,
    });
    CURRENCY_FORMATTERS.set(cacheKey, formatter);
  }
  return formatter.format(amountMinor / 100);
};

const HERO_DRAWER_DIRECTION_LOCK_PX = 8;
const HERO_DRAWER_EXPAND_THRESHOLD_PX = 24;

export function EventPageClient({
  addons = null,
  hiive,
  event,
  occurrences,
  bookingRestriction,
  bookingWindow,
  canBookEvent,
  waitlistOnly = false,
  bookingAvailabilityBlocked,
  canStartGuestBooking,
  canViewAttendeeList,
  canViewAttendeeCount,
  membershipStatus,
  meetsTierRequirement,
  hiiveSlug,
  eventSlug,
  userId,
  viewer,
  hostUserIds,
  viewerAlbumTag,
  nextOccurrence,
  allowedTierNames,
  eligibilityGate,
  pendingTierRequestId = null,
  distanceKm,
  hiiveMemberCount,
  hiiveEventCount,
  packs,
  packStripContext,
  viewerPackHolding,
  storyRingStatus,
  tabCounts,
}: EventPageClientProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { openAuthPrompt } = useAuthPrompt();
  const fromParam = searchParams.get("from");
  const backHref = useMemo(
    () => resolveBackHref(fromParam, "/hiives"),
    [fromParam],
  );
  const handleBrowserBack = useEffectEvent(() => {
    // `useEffectEvent` here is only for reading the latest `fromParam`/
    // `backHref` — do NOT rely on it for a stable identity: the function it
    // returns is re-created on every render. `useBackNavigation` keeps `onBack`
    // in a ref and out of its effect deps, which is what actually guarantees
    // the interceptor mounts once (it pushes a dummy history entry per effect
    // run, and with `cleanupOnUnmount: false` those entries are never popped).
    //
    // The interceptor has already consumed its dummy entry via this popstate,
    // so by now the entry behind us is the real previous page — which
    // `goBackTo` checks against `backHref` before popping. "Something is
    // behind us" was not enough: an event reached from /hiives, left for its
    // hiive and pushed back to, sits on top of the hiive page, so popping took
    // the reader there while this page's own arrow pointed at /hiives.
    goBackTo(router, backHref);
  });

  useBackNavigation({
    behavior: "intercept",
    onBack: handleBrowserBack,
    cleanupOnUnmount: false,
  });
  // Desktop drops the snap-point drawer: the hero sits in a sticky left
  // column and the sheet's contents render inline beside it. The drawer
  // state below still runs, but only the phone reads it.
  const isDesktop = useIsDesktop();
  const [snapPoints, setSnapPoints] = useState<string[]>(["500px", "700px"]);
  const [snap, setSnap] = useState<number | string | null>("500px");
  const snapPointsRef = useRef(snapPoints);
  snapPointsRef.current = snapPoints;
  const snapRef = useRef(snap);
  snapRef.current = snap;
  const expandDrawer = useCallback(() => {
    const expanded = snapPointsRef.current[1];
    if (expanded != null) setSnap(expanded);
  }, []);
  /**
   * Sheet is at its full-height snap point (vs. the collapsed peek). The
   * desktop column has no peek — it is always the whole sheet.
   */
  const isDrawerExpanded = isDesktop || snap === snapPoints[1];
  const { setManualPause } = useVideoAutoplay();
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const heroTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const heroTouchAxisRef = useRef<"x" | "y" | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const sheetPullHandoffCleanupRef = useRef<(() => void) | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const trackedViewRef = useRef(false);
  const initialShouldOpenRef = useRef(
    searchParams.get("open_drawer") === "true",
  );
  const initialShouldFocusTabsRef = useRef(
    searchParams.get("focus_tabs") === "true",
  );
  // A direct link to a non-default tab needs the drawer fully open so its
  // content is immediately usable, not clipped behind the hero.
  const initialTabNeedsExpandedDrawerRef = useRef(
    normalizeTab(searchParams.get("tab")) !== "details",
  );

  const [currentTab, setCurrentTab] = useState(() =>
    normalizeTab(searchParams.get("tab")),
  );
  const [tierDrawerOpen, setTierDrawerOpen] = useState(false);
  const shouldFocusTabs = searchParams.get("focus_tabs") === "true";
  const [isReady, setIsReady] = useState(!shouldFocusTabs);
  const isInitialDrawerAnimationDisabled = useDisableInitialDrawerAnimation();
  const dateParam = searchParams.get("date");
  const slotParam = searchParams.get("slot");

  const normalizedOccurrences = withoutCanceledSessions(
    normalizeOccurrences(occurrences),
  );
  const hasModules = event.module !== null;
  const showAttendeesTab = canViewAttendeeList || canViewAttendeeCount;
  const modulesTabLabel = resolveModuleTabLabel(event.module);
  const bookableOccurrences = normalizedOccurrences.filter(
    (occurrence) => occurrence.status === "scheduled",
  );
  const occurrencesByDate = groupOccurrencesByDate(normalizedOccurrences);
  const dateKeys = getDateKeys(occurrencesByDate);
  const defaultDateKey = (() => {
    if (nextOccurrence) {
      const key = toZonedDateInput(nextOccurrence.start, APP_DEFAULT_TIME_ZONE);
      if (dateKeys.includes(key)) return key;
    }
    return dateKeys[0] ?? null;
  })();

  const slotDateKey = (() => {
    if (!slotParam) return null;
    const occurrence = normalizedOccurrences.find(
      (item) => item.id.toString() === slotParam,
    );
    return occurrence ? toLocalDateKey(occurrence.start) : null;
  })();
  const selectedDateKey =
    slotDateKey ??
    (dateParam && dateKeys.includes(dateParam) ? dateParam : defaultDateKey);
  const occurrencesForSelectedDate = getOccurrencesForSelectedDate(
    occurrencesByDate,
    selectedDateKey,
  );
  const selectedOccurrence = getSelectedOccurrence(
    occurrencesForSelectedDate,
    slotParam,
  );

  const updateUrl = useEffectEvent(
    (
      updates: { tab?: string; date?: string; slot?: string },
      replace = false,
    ) => {
      const params = new URLSearchParams(searchParams.toString());

      if (updates.tab) {
        if (updates.tab === "details") {
          params.delete("tab");
        } else {
          params.set("tab", updates.tab);
        }
      }

      if (updates.date) params.set("date", updates.date);
      if (updates.slot) params.set("slot", updates.slot);

      if (defaultDateKey && updates.date === defaultDateKey)
        params.delete("date");
      if (
        updates.slot &&
        occurrencesForSelectedDate.length > 0 &&
        updates.slot === occurrencesForSelectedDate[0].id.toString()
      ) {
        params.delete("slot");
      }

      const query = params.toString();
      // Client-controlled tabs: instant `setCurrentTab` + shallow URL sync.
      // Use native history with `null` state and relative `?query` so
      // `useSearchParams` updates in-place without server re-render
      // (Activity preserves). `router.push` would add a navigation delay.
      const newUrl = query ? `?${query}` : pathname;

      if (replace) {
        window.history.replaceState(null, "", newUrl);
      } else {
        window.history.pushState(null, "", newUrl);
      }
    },
  );

  const handleTabChange = (value: string) => {
    captureClientEvent(ANALYTICS_EVENTS.EVENT_TAB_CHANGED, {
      event_id: event.id,
      event_name: event.name,
      event_slug: eventSlug,
      hiive_id: hiive.id,
      hiive_slug: hiiveSlug,
      tab: value,
    });
    setCurrentTab(value);
    updateUrl({ tab: value });
    // Expand via ref – `snapPoints` state may still be initial ["500px","700px"]
    // on first click before `useLayoutEffect` computes real points.
    expandDrawer();
  };

  useEffect(() => {
    const tab = normalizeTab(searchParams.get("tab"));
    setCurrentTab(tab);
  }, [searchParams]);

  useEffect(() => {
    if (!hasModules && currentTab === "features") {
      setCurrentTab("details");
      updateUrl({ tab: "details" }, true);
    }
  }, [currentTab, hasModules]);

  useEffect(() => {
    if (showAttendeesTab || currentTab !== "attendees") {
      return;
    }

    setCurrentTab("details");
    updateUrl({ tab: "details" }, true);
  }, [currentTab, showAttendeesTab]);

  /**
   * State, not a ref.
   *
   * The footer CTA reads this as a prop, and a ref mutated inside an effect
   * does not schedule a render — so the CTA was handed `null` on the render
   * that mattered and every Book-tickets click went out unattributed. Resolving
   * still happens in the effect because it touches `sessionStorage`.
   */
  const [entryAttribution, setEntryAttribution] =
    useState<EventEntryAttribution | null>(null);

  useEffect(() => {
    if (trackedViewRef.current) return;
    trackedViewRef.current = true;
    const attribution = resolveEventEntry({
      eventSlug: event.slug,
      route: "event_page",
    });
    setEntryAttribution(attribution);
    captureIdentifiedClientEvent(ANALYTICS_EVENTS.EVENT_VIEWED, userId, {
      event_id: event.id,
      event_slug: event.slug,
      hiive_id: hiive.id,
      hiive_slug: hiive.slug,
      // Segmentation dimension for ad-hoc PostHog exploration only. Queen
      // analytics deliberately does NOT filter viewers on it: queens and other
      // non-members view their own events and must still count as viewers.
      viewer_membership_status: membershipStatus ?? "none",
      // Set when a partner Hiive's announcement brought them here, so a
      // collaboration's reach can be read per partner.
      via_partner_slug:
        new URLSearchParams(window.location.search).get("via") ?? null,
      ...attribution,
    });
  }, [event.id, event.slug, hiive.id, hiive.slug, membershipStatus, userId]);

  const handleDateChange = (date: string) => {
    const firstSlot = occurrencesByDate.get(date)?.[0];
    updateUrl(
      {
        date,
        slot: firstSlot ? firstSlot.id.toString() : undefined,
      },
      true,
    );
  };

  const handleSlotChange = (slotId: string) => {
    updateUrl({ slot: slotId }, true);
  };

  const [showStickyTitle, setShowStickyTitle] = useState(false);
  // The drawer reports its own scroll via `onUpdate`; the desktop page
  // scrolls the window, past the title at the top of the right column.
  const windowScrolledPastTitle = useWindowScrolledPast(120, isDesktop);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [tabsHeight, setTabsHeight] = useState(64);
  const [containerHeight, setContainerHeight] = useState(0);

  // Stable on purpose: it only reads a ref and calls a state setter. It used to
  // be re-created every render, which made `attachRef` a new callback ref each
  // render too — so React detached and re-attached every listener (and a new
  // ResizeObserver) on each render, mid-gesture included.
  const onUpdate = useCallback(() => {
    const element = nodeRef.current;
    if (!element) return;
    setShowStickyTitle((element.scrollTop ?? 0) > 60);
  }, []);

  const attachRef = useCallback(
    (element: HTMLDivElement | null) => {
      nodeRef.current?.removeEventListener("scroll", onUpdate);
      sheetPullHandoffCleanupRef.current?.();
      sheetPullHandoffCleanupRef.current = null;

      nodeRef.current = element;

      if (!element) {
        resizeObserverRef.current?.disconnect();
        resizeObserverRef.current = null;
        return;
      }

      resizeObserverRef.current?.disconnect();
      const observer = new ResizeObserver(() => {
        setContainerHeight(element.clientHeight);
      });
      observer.observe(element);
      resizeObserverRef.current = observer;
      setContainerHeight(element.clientHeight);

      onUpdate();
      element.addEventListener("scroll", onUpdate, { passive: true });
      // No `data-vaul-no-drag` toggling here any more: vaul already refuses to
      // drag while this scroller is off its top, and checks at move time
      // rather than at pointerdown. What vaul cannot do on its own is keep the
      // browser from claiming a pull-down at the top — see the helper.
      sheetPullHandoffCleanupRef.current = attachSheetPullHandoff(element);
    },
    [onUpdate],
  );

  useLayoutEffect(() => {
    const topHeight =
      heroRef.current?.offsetHeight ??
      Math.floor(
        window.innerWidth * ASPECT_RATIO_VALUE.portrait5By4HeightMultiplier,
      );

    const points = detailSheetSnapPoints(topHeight, window.innerHeight);
    setSnapPoints(points);
    const shouldOpen = initialShouldOpenRef.current;
    const shouldFocusTabs = initialShouldFocusTabsRef.current;

    if (
      shouldOpen ||
      shouldFocusTabs ||
      initialTabNeedsExpandedDrawerRef.current
    ) {
      setSnap(points[1]);
    } else {
      setSnap(points[0]);
    }
  }, []);

  // Snap points are derived from innerHeight, which changes when the native
  // keyboard opens (KeyboardResize.Native) or mobile browser chrome
  // shows/hides. Recompute on resize — with mount-time values the drawer
  // slides off-screen the moment the keyboard appears.
  useEffect(() => {
    const onResize = () => {
      const topHeight =
        heroRef.current?.offsetHeight ??
        Math.floor(
          window.innerWidth * ASPECT_RATIO_VALUE.portrait5By4HeightMultiplier,
        );
      const points = detailSheetSnapPoints(topHeight, window.innerHeight);
      const index = snapPointsRef.current.indexOf(snapRef.current as string);
      setSnapPoints(points);
      if (index >= 0) setSnap(points[index]);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const heroElement = heroRef.current;
    const expandedSnapPoint = snapPoints[1];
    if (!heroElement || expandedSnapPoint == null) return;

    const resetHeroGesture = () => {
      heroTouchStartRef.current = null;
      heroTouchAxisRef.current = null;
    };

    const expandDrawer = () => {
      if (snap === expandedSnapPoint) return;
      setSnap(expandedSnapPoint);
    };

    const handleWheel = (event: WheelEvent) => {
      if (
        Math.abs(event.deltaY) <= Math.abs(event.deltaX) ||
        event.deltaY <= 0
      ) {
        return;
      }

      expandDrawer();
    };

    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;

      heroTouchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
      heroTouchAxisRef.current = null;
    };

    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      const start = heroTouchStartRef.current;
      if (!touch || !start) return;

      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;

      if (
        heroTouchAxisRef.current == null &&
        Math.max(Math.abs(deltaX), Math.abs(deltaY)) >=
          HERO_DRAWER_DIRECTION_LOCK_PX
      ) {
        heroTouchAxisRef.current =
          Math.abs(deltaY) > Math.abs(deltaX) ? "y" : "x";
      }

      if (
        heroTouchAxisRef.current === "y" &&
        deltaY <= -HERO_DRAWER_EXPAND_THRESHOLD_PX
      ) {
        expandDrawer();
        resetHeroGesture();
      }
    };

    heroElement.addEventListener("wheel", handleWheel, { passive: true });
    heroElement.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    heroElement.addEventListener("touchmove", handleTouchMove, {
      passive: true,
    });
    heroElement.addEventListener("touchend", resetHeroGesture, {
      passive: true,
    });
    heroElement.addEventListener("touchcancel", resetHeroGesture, {
      passive: true,
    });

    return () => {
      heroElement.removeEventListener("wheel", handleWheel);
      heroElement.removeEventListener("touchstart", handleTouchStart);
      heroElement.removeEventListener("touchmove", handleTouchMove);
      heroElement.removeEventListener("touchend", resetHeroGesture);
      heroElement.removeEventListener("touchcancel", resetHeroGesture);
    };
  }, [snap, snapPoints]);

  useLayoutEffect(() => {
    if (!headerRef.current) return;

    const observer = new ResizeObserver(() => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    });
    observer.observe(headerRef.current);
    setHeaderHeight(headerRef.current.offsetHeight);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!tabsRef.current) return;

    setTabsHeight(tabsRef.current.offsetHeight);

    const observer = new ResizeObserver(() => {
      if (tabsRef.current) {
        setTabsHeight(tabsRef.current.offsetHeight);
      }
    });
    observer.observe(tabsRef.current);

    return () => observer.disconnect();
  }, []);

  // Only allow hero video while the drawer is on the collapsed snap. Any other
  // snap (fully expanded, intermediate, or null during remount) must pause —
  // IntersectionObserver still sees the hero as "in viewport" under the drawer
  // because the drawer is portaled and does not occlude for intersection.
  useEffect(() => {
    // Nothing covers the hero on desktop, so nothing should pause it.
    if (isDesktop) {
      setManualPause(false);
      return;
    }
    if (snapPoints.length < 1) return;
    const isCollapsed = snap === snapPoints[0];
    setManualPause(!isCollapsed);
  }, [isDesktop, snap, snapPoints, setManualPause]);

  useEffect(() => {
    return () => {
      setManualPause(false);
    };
  }, [setManualPause]);

  const bookingUrl = `/hiives/${hiiveSlug}/events/${eventSlug}/book`;
  /**
   * The tiers page, told WHY it is being opened.
   *
   * `?forEvent` is what turns a five-card price grid into an answer: the tiers
   * page resolves the slug server-side, highlights the tier that carries this
   * event, and puts it first. Without it a visitor who came from a shared link
   * is left to infer which membership includes the thing they wanted.
   */
  const joinUrl = `/hiives/${hiiveSlug}/join?forEvent=${encodeURIComponent(eventSlug)}`;
  const restrictedEvent = event.allowedTierIds.length > 0;

  const lowestActivePass = getLowestActivePass(bookableOccurrences);
  const [lowestPriceRatePreview, setLowestPriceRatePreview] =
    useState<RatePreview | null>(null);

  useEffect(() => {
    const currency = lowestActivePass?.currency.toUpperCase();
    if (!currency || currency === SETTLEMENT_CURRENCY) {
      setLowestPriceRatePreview(null);
      return;
    }

    let canceled = false;
    getExchangeRate({ from: currency }).then((result) => {
      if (canceled) return;
      setLowestPriceRatePreview(
        result.ok
          ? {
              rate: result.data.rate.rate,
              date: result.data.rate.date,
              fetchedAtIso: result.data.rate.fetchedAtIso,
            }
          : null,
      );
    });

    return () => {
      canceled = true;
    };
  }, [lowestActivePass?.currency]);

  const lowestPassIsDeposit = Boolean(
    lowestActivePass?.isDeposit && lowestActivePass.totalPriceMinor != null,
  );
  const depositSuffix = lowestPassIsDeposit ? " deposit" : "";
  // "Onwards" only when some other pass or date costs more.
  const onwardsSuffix = lowestActivePass?.priceVaries ? " onwards" : "";
  // "₹0 onwards" is not a price anybody says out loud.
  const lowestPassIsFree = lowestActivePass?.amountMinor === 0;
  const defaultPriceSummaryText = lowestActivePass
    ? lowestPassIsFree
      ? "Free"
      : lowestPriceRatePreview
        ? `${formatCurrency(
            Math.round(
              lowestActivePass.amountMinor * lowestPriceRatePreview.rate,
            ),
            SETTLEMENT_CURRENCY,
          )}${depositSuffix}${onwardsSuffix} · listed as ${formatCurrency(
            lowestActivePass.amountMinor,
            lowestActivePass.currency,
          )}`
        : `${formatCurrency(lowestActivePass.amountMinor, lowestActivePass.currency)}${depositSuffix}${onwardsSuffix}`
    : "View ticket options";
  const depositSummaryText =
    lowestPassIsDeposit && lowestActivePass?.totalPriceMinor != null
      ? `${formatCurrency(
          lowestActivePass.totalPriceMinor,
          lowestActivePass.currency,
        )} total · balance paid to the organizer`
      : null;

  const shouldShowEligibilityCta =
    (!userId && !canStartGuestBooking && !bookingAvailabilityBlocked) ||
    (restrictedEvent &&
      (membershipStatus !== "active" || !meetsTierRequirement));
  const shouldShowTierInfo =
    restrictedEvent && !meetsTierRequirement && allowedTierNames.length > 0;

  // The footer and the schedule row read the same rule, so they cannot end up
  // describing the same window differently. Both stay on the server's plain
  // restriction string until mount: the rich copy is relative to the viewer's
  // clock and timezone, which server-rendered HTML must not bake in.
  const pendingWindowOccurrence =
    useMemo<BookingWindowOccurrence | null>(() => {
      const opensAt = toDate(bookingWindow.nextOpensAt);
      if (!opensAt) return null;
      const occurrenceStart =
        toDate(bookingWindow.nextOpensOccurrenceStart) ?? opensAt;
      return {
        id: -1,
        start: occurrenceStart,
        end: occurrenceStart,
        bookingOpensAt: opensAt,
        bookingClosesAt: null,
      };
    }, [bookingWindow.nextOpensAt, bookingWindow.nextOpensOccurrenceStart]);

  const footerNow = useNow((current) =>
    pendingWindowOccurrence
      ? (resolveBookingWindowState({
          now: current,
          occurrence: pendingWindowOccurrence,
        })?.tickMs ?? null)
      : null,
  );

  const pendingWindowState =
    footerNow && pendingWindowOccurrence
      ? resolveBookingWindowState({
          now: footerNow,
          occurrence: pendingWindowOccurrence,
        })
      : null;

  /**
   * The two lines a closed event shows, and who wrote them.
   *
   * The old copy was `Bookings closed 3 days ago. No new window is scheduled.`
   * — accurate, and an answer to a question nobody asked. What a visitor
   * standing outside a venue needs is what to do next, and the only person who
   * knows that is whoever runs the event. So a chosen preset wins outright,
   * with the organiser's own note under it; the generated sentence is the
   * fallback for events whose owner never said anything.
   */
  const bookingClosedCopyForFooter =
    bookingRestriction === "Bookings for this event are currently closed."
      ? bookingClosedCopy({
          cta: event.bookingClosedCta,
          message: event.bookingClosedMessage,
          fallbackHeadline: (() => {
            const closedAt = toDate(bookingWindow.lastClosedAt);
            return closedAt && footerNow
              ? `Bookings closed ${formatMoment(closedAt, footerNow)}`
              : "Bookings are closed";
          })(),
        })
      : null;

  const bookingWindowText = (() => {
    if (!bookingRestriction) return null;
    if (bookingRestriction === "Bookings are not open yet.") {
      // Any other phase means the client clock has already crossed the
      // opening; the row's route refresh is on its way, so say nothing new.
      return pendingWindowState?.phase === "pre_open"
        ? pendingWindowState.headline
        : bookingRestriction;
    }
    if (bookingClosedCopyForFooter) return bookingClosedCopyForFooter.headline;
    // Nothing left on the calendar and the last date ran: say the event is
    // over, in those words. A canceled final date keeps the plain sentence.
    if (bookingRestriction === NO_UPCOMING_DATES_RESTRICTION) {
      return selectedOccurrence?.status === "completed"
        ? "This event has ended"
        : "No upcoming dates";
    }
    return bookingRestriction;
  })();

  // A guest we are letting through sees the price, not a wall: the whole point
  // is that the footer looks identical to a member's until the moment money is
  // involved. See `plans/guest-checkout-auth.md`.
  /**
   * The footer caption for a tier wall.
   *
   * Reads `eligibilityGate` — the same value the CTA switches on — so the
   * caption and the button can never end up describing different walls.
   */
  const tierWallText = (() => {
    if (eligibilityGate?.kind === "request_pending") {
      return "Your request is with the organisers.";
    }
    if (
      eligibilityGate?.kind !== "needs_membership" &&
      eligibilityGate?.kind !== "wrong_tier"
    ) {
      return null;
    }
    // Kept short: the footer gives this caption roughly half a phone width
    // next to the button, and it now carries the price too — "Free · Homies
    // tier only" already wrapped to three lines on a 375px screen.
    if (allowedTierNames.length === 1) {
      return `${allowedTierNames[0]} only`;
    }
    if (allowedTierNames.length > 1) {
      return `${allowedTierNames.length} tiers get in`;
    }
    return "Members only";
  })();

  const footerText = (() => {
    if (canBookEvent || canStartGuestBooking) return defaultPriceSummaryText;

    // A wall is not a reason to hide the price. Somebody deciding whether a
    // membership is worth it needs to know the event behind it is free — the
    // tier wall on its own reads like a paywall, which for Homies it is not.
    if (tierWallText) {
      return lowestActivePass
        ? `${defaultPriceSummaryText} · ${tierWallText}`
        : tierWallText;
    }

    return bookingWindowText ?? "Booking unavailable";
  })();

  /**
   * The members-only detour, recorded against THIS event.
   *
   * `tier_viewed` / `tier_join_requested` already exist but are scoped to the
   * hiive, and every event-analytics query filters on `event_slug` — so for a
   * tier-gated event the queen saw arrivals and then a cliff, unable to tell
   * "nobody wanted it" from "everybody hit the wall and went to ask for
   * access". These two are the viewer-side half of that story.
   */
  const tierGateKind =
    eligibilityGate?.kind === "needs_membership" ||
    eligibilityGate?.kind === "wrong_tier" ||
    eligibilityGate?.kind === "request_pending"
      ? eligibilityGate.kind
      : null;

  const tierGateProperties = useMemo(
    () => ({
      event_id: event.id,
      event_slug: eventSlug,
      hiive_id: hiive.id,
      // Both slugs, deliberately: the analytics scope matches on `event_slug`
      // and prefers `hiive_slug`, and an emitter that sends only one is the
      // bug that silently dropped every `event_booking_expired` once already.
      hiive_slug: hiiveSlug,
      gate_kind: tierGateKind,
      allowed_tier_names: allowedTierNames,
      allowed_tier_count: allowedTierNames.length,
    }),
    [allowedTierNames, event.id, eventSlug, hiive.id, hiiveSlug, tierGateKind],
  );

  // Once per (event, gate kind), not once per render. Keyed on the kind so a
  // viewer who asks for access and comes back counts once as "walled" and once
  // as "waiting" rather than twice as the same thing.
  const trackedGateRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tierGateKind) return;
    const key = `${eventSlug}:${tierGateKind}`;
    if (trackedGateRef.current === key) return;
    trackedGateRef.current = key;
    // Identified where possible: a signed-out viewer who later signs in has
    // their anonymous rows merged, which is what lets this branch join the
    // rest of the funnel for the same person.
    captureIdentifiedClientEvent(
      ANALYTICS_EVENTS.EVENT_TIER_GATE_VIEWED,
      userId,
      tierGateProperties,
    );
  }, [eventSlug, tierGateKind, tierGateProperties, userId]);

  /** "Join Homies to book", not "Join hiive" — the tier IS the instruction. */
  const tierCtaLabel =
    allowedTierNames.length === 1
      ? `Join ${allowedTierNames[0]} to book`
      : allowedTierNames.length > 1
        ? "See which tier you need"
        : "Join this hiive to book";

  const cta = (() => {
    // Sold out is not the end when a ticket keeps a line: send people to the
    // booking page, where joining takes one tap.
    if (waitlistOnly && (canBookEvent || canStartGuestBooking))
      return { href: bookingUrl, label: "Join waitlist", disabled: false };
    if (
      bookingRestriction === "Sold out" ||
      (bookingClosedCopyForFooter && event.bookingClosedCta === "sold_out")
    )
      return { href: null, label: "Sold out", disabled: true };
    if (canBookEvent || canStartGuestBooking) {
      return { href: bookingUrl, label: "Book tickets", disabled: false };
    }

    // A closed/not-yet-open event is not an identity problem. Keep the footer
    // visible for its availability message, but do not offer a sign-in that
    // cannot change the outcome.
    if (bookingAvailabilityBlocked) {
      return null;
    }

    /*
     * One switch over the server's `eligibilityGate`, which resolves the TIER
     * wall before the sign-in wall.
     *
     * That ordering is the fix. A tier-gated event shared on Instagram is seen
     * almost entirely by signed-out people, and the old code sent every one of
     * them to a sign-in drawer that could not unblock them — they signed in and
     * hit the real wall one screen later, still with no idea which of five
     * tiers was the right one. Naming the tier up front lets them decide before
     * they hand over a phone number.
     */
    switch (eligibilityGate?.kind) {
      case "request_pending":
        // Neither "Join hiive" (they did) nor a live join button: a second
        // request is refused outright by the one-pending-per-hiive index.
        //
        // Straight to THEIR request when we know which one it is. The list was
        // one more screen between somebody chasing an answer and the page that
        // has it.
        return {
          href: pendingTierRequestId
            ? `/membership-requests/${pendingTierRequestId}`
            : "/membership-requests",
          label: "Request pending",
          disabled: false,
        };
      case "needs_membership":
      case "wrong_tier":
        return { href: joinUrl, label: tierCtaLabel, disabled: false };
      case "needs_pack":
        // Points at the packs page rather than refusing. This session is part
        // of something that is on sale right now, so the honest CTA is the
        // thing that gets them in — not a greyed-out button.
        return {
          href: hiiveSlug ? `/hiives/${hiiveSlug}/packs` : null,
          label: eligibilityGate.packName
            ? `Get the ${eligibilityGate.packName}`
            : "Members' pass required",
          disabled: !hiiveSlug,
        };
      case "sign_in":
        // Everything but identity is already satisfied, and the guest path did
        // not claim this viewer — `canStartGuestBooking` covers the public and
        // gender-restricted cases, so this is the fallback for a shape the two
        // rules disagree about. Signing in really is the next step here.
        return {
          href: null,
          label: "Sign in",
          disabled: false,
          onClick: () =>
            openAuthPrompt({
              title: "Sign in to book",
              description:
                "Use your Hiive account to continue booking this event.",
            }),
        };
      default:
        return null;
    }
  })();

  const shouldShowFooterCta =
    (canBookEvent ||
      canStartGuestBooking ||
      shouldShowEligibilityCta ||
      Boolean(bookingRestriction)) &&
    currentTab !== "features";

  // Sessions, not the pattern, decide: a one-time event can still run several
  // times that day (see lib/events/event-pattern).
  const hasSessionChoice =
    isMultiDate(event.pattern) || normalizedOccurrences.length > 1;
  const showSlotSelectors =
    hasSessionChoice &&
    (currentTab === "attendees" || currentTab === "features");

  const activeOccurrence = selectedOccurrence
    ? {
        id: selectedOccurrence.id,
        startTime: selectedOccurrence.start,
        endTime: selectedOccurrence.end,
        timezone: selectedOccurrence.timezone,
        attendeeCount: selectedOccurrence.attendeeCount,
      }
    : nextOccurrence
      ? {
          id: nextOccurrence.id,
          startTime: nextOccurrence.start,
          endTime: nextOccurrence.end,
          timezone: nextOccurrence.timezone,
          attendeeCount:
            normalizedOccurrences.find(
              (occurrence) => occurrence.id === nextOccurrence.id,
            )?.attendeeCount ?? 0,
        }
      : null;
  const activeScheduleOccurrence =
    selectedOccurrence ??
    (nextOccurrence
      ? (normalizedOccurrences.find(
          (occurrence) => occurrence.id === nextOccurrence.id,
        ) ?? null)
      : null);

  const selectedOccurrenceStatus = selectedOccurrence?.status ?? null;
  const isViewingClosedOccurrence =
    selectedOccurrenceStatus != null &&
    selectedOccurrenceStatus !== "scheduled";

  const minContentHeight =
    containerHeight > 0
      ? containerHeight - headerHeight - tabsHeight - 80 - 20
      : 0;

  useLayoutEffect(() => {
    if (!shouldFocusTabs) {
      setIsReady(true);
      return;
    }

    // Desktop has no drawer to expand — the tabs are already on the page, so
    // scroll the window to them and reveal.
    if (isDesktop) {
      const tabs = tabsRef.current;
      if (tabs) {
        window.scrollTo({
          top: Math.max(
            0,
            tabs.getBoundingClientRect().top + window.scrollY - headerHeight,
          ),
          behavior: "instant",
        });
      }
      setIsReady(true);
      return;
    }

    if (
      headerHeight > 0 &&
      tabsRef.current &&
      nodeRef.current &&
      snap === snapPoints[1]
    ) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (nodeRef.current && tabsRef.current) {
            const stickyTop =
              parseFloat(
                getComputedStyle(tabsRef.current).top?.replace("px", "") ?? "0",
              ) || Math.max(0, headerHeight - 57);
            const elementOffset = tabsRef.current.offsetTop;
            const targetScroll = elementOffset - stickyTop - 45;

            nodeRef.current.scrollTo({
              top: targetScroll,
              behavior: "instant",
            });

            requestAnimationFrame(() => setIsReady(true));
          }
        });
      });
    }
  }, [isDesktop, shouldFocusTabs, headerHeight, snap, snapPoints]);

  const renderFooterCta = (variant: "floating" | "inline") =>
    shouldShowFooterCta ? (
      <EventFooterCta
        variant={variant}
        priceSummaryText={
          isViewingClosedOccurrence && (canBookEvent || canStartGuestBooking)
            ? "This date is over · Book an upcoming one"
            : footerText
        }
        secondarySummaryText={
          (canBookEvent || canStartGuestBooking) && !isViewingClosedOccurrence
            ? depositSummaryText
            : // The organiser's own sentence, under their chosen headline.
              (bookingClosedCopyForFooter?.message ?? null)
        }
        canBookEvent={canBookEvent || canStartGuestBooking}
        bookingUrl={cta?.href ?? null}
        selectedOccurrenceId={selectedOccurrence?.id}
        event={event}
        hiive={hiive}
        isCtaDisabled={cta?.disabled ?? true}
        ctaLabel={cta?.label ?? "Booking unavailable"}
        onCtaClick={cta?.onClick}
        onCtaNavigate={
          tierGateKind
            ? () =>
                captureIdentifiedClientEvent(
                  ANALYTICS_EVENTS.EVENT_TIER_GATE_CTA_CLICKED,
                  userId,
                  tierGateProperties,
                )
            : undefined
        }
        onInfoClick={
          shouldShowTierInfo ? () => setTierDrawerOpen(true) : undefined
        }
        infoLabel="Show eligible tiers"
        viewerUserId={userId}
        entryAttribution={entryAttribution}
      />
    ) : null;

  const sheetContents = (
    <>
      <DrawerHeader
        className={cn("px-4 pt-4 pb-0 !text-left", isDesktop && "pt-0")}
      >
        <div className="flex flex-col items-start gap-2">
          <EventHostCredit
            host={{
              name: hiive.name,
              slug: hiiveSlug,
              logoImage: hiive.logoImage,
            }}
            collaborators={event.collaborators}
          />
          {/* The collapsed peek is a fixed height (viewport minus the
              hero), so a long name like "Sound Healing Experience with
              Red Light and Candles followed by Herbea Infused Tea" ran
              to four lines and pushed the tabs and the first row of
              content underneath the booking bar. Clamp it while
              collapsed; expanding the sheet reveals the full name. The
              desktop column never collapses, so it never clamps. */}
          {isDesktop ? (
            <h1 className="text-3xl font-bold text-foreground">{event.name}</h1>
          ) : (
            <DrawerTitle
              className={cn(
                "text-2xl font-bold",
                !isDrawerExpanded && "line-clamp-2",
              )}
              title={event.name}
            >
              {event.name}
            </DrawerTitle>
          )}
          <EventDateSummary
            pattern={event.pattern}
            nextOccurrence={nextOccurrence}
            dateKeys={dateKeys}
            timesOnNextDate={
              nextOccurrence
                ? bookableOccurrences.filter(
                    (occurrence) =>
                      toLocalDateKey(occurrence.start) ===
                      toLocalDateKey(nextOccurrence.start),
                  )
                : []
            }
            openEndedTime={event.openEndedTime}
          />
          <EventAddonsPill />
        </div>
      </DrawerHeader>

      <EventTabsSection
        layout={isDesktop ? "page" : "drawer"}
        hasUpcomingDates={bookableOccurrences.length > 0}
        currentTab={currentTab}
        onTabChange={handleTabChange}
        discussionComposerVisible={isDrawerExpanded}
        onExpandDrawer={expandDrawer}
        headerHeight={headerHeight}
        tabsRef={tabsRef}
        minContentHeight={minContentHeight}
        hiiveSlug={hiiveSlug}
        showSlotSelectors={showSlotSelectors}
        dateKeys={dateKeys}
        selectedDateKey={selectedDateKey}
        handleDateChange={handleDateChange}
        occurrencesForSelectedDate={occurrencesForSelectedDate}
        eventPattern={event.pattern}
        selectedOccurrence={selectedOccurrence}
        handleSlotChange={handleSlotChange}
        activeOccurrence={activeOccurrence}
        activeScheduleOccurrence={activeScheduleOccurrence}
        scheduleOccurrences={normalizedOccurrences}
        canViewAttendeeList={canViewAttendeeList}
        canViewAttendeeCount={canViewAttendeeCount}
        showAttendeesTab={showAttendeesTab}
        activities={event.activities}
        module={event.module}
        hasModules={hasModules}
        modulesTabLabel={modulesTabLabel}
        userId={userId}
        viewer={viewer}
        hostUserIds={hostUserIds}
        viewerAlbumTag={viewerAlbumTag}
        event={event}
        distanceKm={distanceKm}
        hiive={hiive}
        hiiveMemberCount={hiiveMemberCount}
        hiiveEventCount={hiiveEventCount}
        packs={packs}
        packStripContext={packStripContext}
        viewerPackHolding={viewerPackHolding}
        tabCounts={tabCounts}
      />
    </>
  );

  return (
    <EventAddonsProvider
      menu={addons}
      bookHref={cta?.href ?? null}
      previewOccurrenceId={activeOccurrence?.id ?? null}
    >
      <div
        className={cn(
          "mx-auto flex h-screen max-w-2xl flex-col",
          DETAIL_DESKTOP.grid,
        )}
      >
        <EventStickyHeader
          eventId={String(event.id)}
          hiive={hiive}
          eventName={event.name}
          headerRef={headerRef}
          showStickyTitle={
            isDesktop ? windowScrolledPastTitle : showStickyTitle
          }
        />

        <div className={DETAIL_DESKTOP.mediaColumn}>
          <EventHeroSection
            heroRef={heroRef}
            eventId={event.id}
            // "Sold out" on a date that is over is noise, not information.
            availability={
              selectedOccurrence?.status === "scheduled"
                ? eventAvailability(selectedOccurrence)
                : undefined
            }
            eventName={event.name}
            bannerMedia={event.bannerMedia}
            bannerMusic={event.bannerMusic}
            isReady={isReady}
            storyRingStatus={storyRingStatus}
            className={DETAIL_DESKTOP.media}
          />
          {isDesktop ? renderFooterCta("inline") : null}
        </div>

        {isDesktop ? (
          <div
            className={cn(
              "event-detail-sheet flex flex-col transition-opacity duration-150",
              DETAIL_DESKTOP.content,
              isReady ? "opacity-100" : "opacity-0",
            )}
          >
            {sheetContents}
          </div>
        ) : (
          <Drawer
            dismissible={false}
            modal={false}
            snapPoints={snapPoints}
            activeSnapPoint={snap}
            setActiveSnapPoint={setSnap}
            defaultOpen
          >
            <DrawerContent
              showHandle={false}
              className={cn(
                isInitialDrawerAnimationDisabled &&
                  DISABLE_DRAWER_ANIMATION_CLASS,
                // `page-sheet`: the page content, not a modal (globals.css).
                // No focus ring: it takes focus on open, and WebKit outlined
                // the whole page in blue.
                "page-sheet mt-0 h-full overflow-hidden border-none bg-transparent text-text-primary outline-none",
              )}
            >
              <div
                ref={attachRef}
                className={cn(
                  "event-detail-sheet mx-auto flex h-full min-h-0 w-full max-w-2xl flex-col border-t border-background bg-background pb-20 transition-opacity duration-150",
                  isReady ? "opacity-100" : "opacity-0",
                  isDrawerExpanded
                    ? "overflow-y-auto pt-[calc(env(safe-area-inset-top)+3.5rem)] side-nav:pt-[max(env(safe-area-inset-top),0.75rem)]"
                    : "overflow-hidden",
                )}
              >
                {sheetContents}
              </div>
            </DrawerContent>
          </Drawer>
        )}

        {/* On the discussion tab the comment composer owns the bottom edge —
          the booking CTA would sit on top of it. Desktop carries the CTA in
          the media column instead, clear of the discussion column. */}
        {!isDesktop && currentTab !== "discussion"
          ? renderFooterCta("floating")
          : null}

        <Drawer open={tierDrawerOpen} onOpenChange={setTierDrawerOpen}>
          <DrawerContent className="z-1000 mx-auto max-w-2xl border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white">
            <DrawerHeader className="border-b border-border text-left dark:border-white/[0.1]">
              <DrawerTitle className="text-text-primary dark:text-white">
                Eligible tiers
              </DrawerTitle>
            </DrawerHeader>
            <div className="space-y-4 px-5 pt-2 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]">
              <p className="text-sm leading-relaxed text-text-secondary dark:text-white/64">
                This event is reserved for members with one of these tiers.
              </p>
              <div className="grid gap-2">
                {allowedTierNames.map((tierName) => (
                  <div
                    key={tierName}
                    className="rounded-2xl border border-border bg-surface-alt px-4 py-3 text-sm font-semibold text-text-primary dark:border-glass-border-08 dark:bg-glass-bg-04 dark:text-white"
                  >
                    {tierName}
                  </div>
                ))}
              </div>
              <BackAwareLink
                href={joinUrl}
                className="flex h-12 w-full items-center justify-center rounded-2xl bg-brand text-sm font-semibold text-brand-foreground dark:bg-white dark:text-black"
              >
                Open hiive tiers
              </BackAwareLink>
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </EventAddonsProvider>
  );
}
